import { iws_isInSearchState } from "../basic";
import { IsiDtoPlXmlUserSettingsResult } from "../Dtos/Isilog.IsiComponent.IsiDataTransferObject.IsiPlXmlUserSettings";
import { IsiPlXmlUserSettings } from "../Isilog/PresentationLogic/IsiPlXmlUserSettings";
import { IwsFifo } from "./IwsFifo";
import { Semaphore } from "./Semaphore";
import { IsiXmlBuffer } from "./XmlBuffer";
import { XmlLog } from "./XmlLog";
import { XmlTrace } from "./XmlTrace";
import { XmlUserSettingsResult } from "./XmlUserSettingsResult";

declare var TYPE_VALDEF: string;
declare var TYPE_FILTER: string;
declare var TYPE_DESIGN: string;
declare var TYPE_TOOLTIP: string;
declare const CTRL_ISIDATALISTCTRL: number;

// Allows up to 6 parallel operations to run
const semaphoreXml = new Semaphore('IsiXmlUserSettings', 1)

type Events = 'afterAllCalcValDef'
export const EVENT_AFTER_CALC_VAL_DEF = 'afterAllCalcValDef'

type States = {
    defaultValueTriggerOnChange: boolean,
    relatedCalculationInProgress: boolean
}

type Action = () => any

export class IsiXmlUserSettings extends EventTarget {

    private _log: XmlLog;
    private _iurc_depth: number = 0;
    private _evalCount: number = 0;
    private _traceMode: string = 'defaultValue'; // defaultValue, filter, design

    // Utilisation d’un proxy qui centralise la gestion des abonnements attachés à des états
    private _states: States = new Proxy({ defaultValueTriggerOnChange: false, relatedCalculationInProgress: false }, {
        set: (target, prop: keyof States, value) => {
            target[prop] = value

            if ((!value && prop === 'defaultValueTriggerOnChange' && !target.relatedCalculationInProgress) || (!value && prop === 'relatedCalculationInProgress' && !target.defaultValueTriggerOnChange)) {
                this.observables.get(EVENT_AFTER_CALC_VAL_DEF).forEach(action => {
                    action()
                })
            }

            return true
        },
        get: (target, prop) => {
            return target[prop]
        }
    })

    private _uniqueId: string = "none";
    private static _classCount: number = 0;
    private _uniqueCount: number;
    private _newAlgoXmlSort: boolean = true; // permet de revenir à l'algo d'origine
    public ctrlsDesignEvaluated = {};
    private readonly _eventRelatedCalculationFinish = "eventRelatedCalculationFinish";

    // L'observable centralise toutes les actions s’abonnant à un événement.
    // Le set garantis que les actions ne serront exécuté qu'une fois
    private observables: Map<Events, Set<() => any>> = new Map([[
        EVENT_AFTER_CALC_VAL_DEF, new Set()
    ]]);

    constructor() {
        super();
        IsiXmlUserSettings._classCount += 1;
        this._uniqueCount = IsiXmlUserSettings._classCount;
        this._log = new XmlLog('****');
    }

    // État indiquant si le val def sont en cours de traitement
    get _relatedCalculationInProgress() {
        return this._states.relatedCalculationInProgress
    }

    // Définis l'état indiquant si les vals def sont en cours de traitements
    set _relatedCalculationInProgress(inProgess: boolean) {
        this._states.relatedCalculationInProgress = inProgess
    }

    get _defaultValueTriggerOnChange() {
        return this._states.defaultValueTriggerOnChange
    }

    set _defaultValueTriggerOnChange(inProgess: boolean) {
        this._states.defaultValueTriggerOnChange = inProgess
    }


    /**
     * Permet de s’abonner à un événement.
     * @param event
     * @param action
     * @returns
     */
    public subscribe(event: Events, action: Action) {
        const actions = this.observables.get(event)

        if (!actions.has(action)) {
            actions.add(action)
        }

        this.observables.set(event, actions)

        // Si l’on perd la référence de l’action, on ne pourra plus se désabonner. Retourner une fonction de désabonnement permet de dépasser cette limite
        return () => {
            this.unsubscribe(event, action)
        }
    }

    /**
     * Permet de se désabonner d’un événement.
     * @param event
     * @param action
     */
    public unsubscribe(event: Events, action: Action) {
        const actions = this.observables.get(event)

        if (actions.has(action)) {
            actions.delete(action)
        }
    }

    /**
     *  Definit la valeur de la propriété evaluationDone
     * @param ctrl
     * @param xmlDataType
     * @param value
     */
    public setEvaluationDone(ctrl: IwsControle, xmlDataType: string, value: boolean): void {

        // LBRI - 2010.11.15 - [DYSF:39572] : evaluationDone n'est plus sur le  controle mais dans IsiXmlData.

        const xmlData = ctrl[xmlDataType];
        if (xmlData && typeof (xmlData) != "undefined") {
            xmlData.evaluationDone = value;
        }
    }

    /**
     * Lance l'évaluation de toutes les valdef
     * @param ctrl Le contrôle à l'origine du déclenchement
     */
    public async calcAllXml(uniqueId: string, ctrl: IwsControle, eventDetail?: any) {
        let response: string;

        this._evalCount++;

        const lock = await semaphoreXml.acquire();

        response = await this._launchCalcAllXml(uniqueId, ctrl, eventDetail);

        this._evalCount--;

        lock.release();

        return response;
    }

    /**
    * Lance l'évaluation de toutes les valdef
    * @param ctrl Le contrôle à l'origine du déclenchement
    */
    private async _launchCalcAllXml(uniqueId: string, ctrl: IwsControle, eventDetail?: any) {
        let response: string;
        try {

            this._registerUniqueId(uniqueId);

            // DGRA - 03/05/2011 - DEV41536 : Gestion des tooltips dynamiques. On indique qu'il faudra reevaluer lies au controle modifie
            this._dropXmlTooltip(ctrl, iwsAllCtrls);

            this._log.log(this._iurcDepth() + ' Debut -- _launchCalcAllXml' + ' -- niveau.iurc=' + this._iurc_depth);

            // si on est en recherche 
            if (iws_isInSearchState()) {
                response = await this._callAsyncValDefInSearchState(ctrl);
            } else {
                // WIT 34083                
                response = await this._callAsyncValDef(ctrl);
            }

            this._log.log(this._iurcDepth() + ' Fin -- _launchCalcAllXml -- ' + response + ' -- niveau.iurc=' + this._iurc_depth);
            this._log.log(this._iurcDepth() + ' dispatchEvent isiAfterXml');

            ctrl.afterOnchange(eventDetail);

        } catch (error) {
            uiCore.logError('_launchCalcAllXml - Erreur lors du calcul Xml', error);
        }
        return response;
    }

    /**
     * Lance l'évaluation de la valeur Xml du controle.
     * @param trigger le controle à l'origine du déclenchement de cette évaluation
     * @param ctrl le controle dont on veut evaluer la valeur par defaut
     * @param xmlDataType le type : Val def, design, filtre
     * @param typeEval le type d'evaluation a lancer coter serveur
     * @param xmlBuffer le buffer dans lequel on ajoute les actions Xml à traiter
     */
    private _evaluateXml(trigger: IwsControle, ctrl: IwsControle, xmlDataType: string, typeEval: string, xmlBuffer: IsiXmlBuffer, xmlTrc: XmlTrace): void {
        /// <entry mat="LBRI" date="23/03/2006">Création</entry>
        /// <entry mat="ABON" date="22/07/2009" task="DEV26145">Fusion des fonction d'evaluation de filtre et valdef afin de pouvoir gerer le design</entry>
        /// <entry mat="ABON" date="22/07/2009" bug="35266">On change le separateur # pour #S# </entry>
        /// <entry mat="LBRI" date="24/06/2015" task="DEV108612">Modification</entry>

        if (!ctrl) {
            return;
        }
        if (xmlTrc) {
            xmlTrc.pushLinkTrace(ctrl);
        }

        const xmlData = ctrl[xmlDataType];
        const sLinkedCtrlValues = ctrl.getLinkedCtrl(iwsAllCtrls, xmlData, xmlDataType);
        let filterValue = null;

        // xmlType, linkedCtrlValues, ctrlId, triggerId, ctrlValue
        if (xmlDataType === TYPE_FILTER) {
            filterValue = ctrl.valueWithContext({ isValdef: true });
        }
        xmlBuffer.addAction(this._uniqueId, typeEval, sLinkedCtrlValues, ctrl.id, trigger.id, filterValue);

    }

    private _registerUniqueId(uniqueId: string) {
        this._uniqueId = "inst:" + this._uniqueCount + ", call:" + uniqueId;
        this._log.setId(uniqueId);
    }

    /**
     * Lance l'évaluation de la valeur Xml du controle.
     * @param trigger le controle à l'origine du déclenchement de cette évaluation
     * @param ctrl le controle dont on veut evaluer la valeur par defaut
     * @param xmlDataType le type : Val def, design, filtre
     * @param typeEval le type d'evaluation a lancer coter serveur
     * @param xmlTrc
     * @returns
     */
    public async evaluateXmlDirect(uniqueId: string, trigger: IwsControle, ctrl: IwsControle, xmlDataType: string, typeEval: string, xmlTrc: XmlTrace) {
        /// <entry mat="LBRI" date="23/03/2006">Création</entry>
        /// <entry mat="ABON" date="22/07/2009" task="DEV26145">Fusion des fonction d'evaluation de filtre et valdef afin de pouvoir gerer le design</entry>
        /// <entry mat="ABON" date="22/07/2009" bug="35266">On change le separateur # pour #S# </entry>
        /// <entry mat="LBRI" date="24/06/2015" task="DEV108612">Modification</entry>

        if (!ctrl) {
            return;
        }

        if (xmlTrc) {
            xmlTrc.pushLinkTrace(ctrl);
        }

        this._registerUniqueId(uniqueId);
        this._log.log("evaluateXmlDirect : appel direct de IsiPlXmlUserSettings.GetDiscretXmlUserSettings");

        const sLinkedCtrlValues = ctrl.getLinkedCtrl(iwsAllCtrls, ctrl[xmlDataType], xmlDataType);

        // LBRI - 2010.09.10 - [PERF] : afin d'eviter un trop grand nombre d'appel ajax vers les
        // memes valdef, on determine celles qui sont "stables" ou qui le deviennent lors de l'execution.
        // Une fois calculees, les valdefs stables ne sont plus calculees. Ceci n'est vrai que lors du chargement d'ecran.
        // Une valdef est dite stable lorsqu'elle a deja ete calculee et qu'elle ne repose
        // que sur des controles sans valdef, des controles a valdef statique ou des controles a valdef stable.

        // TODO WIT 34082 - clean and test

        this._log.log("evaluateXmlDirect : appel AJAX <=============");

        let xmlClientCtrlValue: string;
        if (xmlDataType == TYPE_FILTER) {
            xmlClientCtrlValue = ctrl.valueWithContext({ isValdef: true });
        }
        const pl = new IsiPlXmlUserSettings();
        const data: IsiDtoPlXmlUserSettingsResult = await pl.GetDiscretXmlUserSettings(typeEval, sLinkedCtrlValues, ctrl.id, trigger.id, xmlClientCtrlValue);
        const xus = new XmlUserSettingsResult(this._uniqueId);
        xus.evaluateXmlResult({ Val: data.value, Ctrl: ctrl.id, Type: typeEval }, this.ctrlsDesignEvaluated);

        this._log.log("evaluateXmlDirect : fin de l'appel direct de IsiPlXmlUserSettings.GetDiscretXmlUserSettings");

    }

    /**
     * Demande l'evaluation des valeurs par defaut de tous les controles de la page.
     * @param xmlDataType le type : Val def  / design /filtre
     * @returns
     */
    public evaluateAllXmlData(uniqueId: string, xmlDataType: string): void {
        //  ABON 22/07/2009 DEV : 26145 - Analyse dependent du type de valeur par def / filtre / design
        if (!iwsAllCtrls) {
            return;
        }
        if (!iwsAllCtrls.tableau) {
            return;
        }

        // LBRI - 2010.01.20 - [DYSF:34110] : Il ne faut evaluer les valdefs que lors du premier postage et pas lors des postbacks.
        if (xmlDataType == TYPE_VALDEF && !iws_isFirstLoad()) {
            return;
        }

        this._registerUniqueId(uniqueId);

        // DGRA - 13/04/2011 - DEV41528 - Refonte du controle date. Remplacement du tableau par une liste.
        if (xmlDataType == TYPE_VALDEF) {

            this._defaultValueTriggerOnChange = true;

            for (let i in iwsAllCtrls.tableau) {
                const ctrl = iwsAllCtrls.tableau[i];
                if (ctrl && ctrl.isModifiedByDefaultValue() == true) {
                    const htmlCtrl = ctrl.getInputElement();
                    const eventDetail = { uniqueId: uniqueId };
                    try {
                        // on declenche le Onchange
                        ctrl._trigger_OnChange(htmlCtrl, eventDetail);
                    } catch {
                        //on s'assure que le flag est bien reset en cas d'erreur
                        this._defaultValueTriggerOnChange = false;
                    }

                }
            }

            this._defaultValueTriggerOnChange = false;
            uiCore.launchSubmitIfPending();

        }

        return;

    }

    /**
     * Indique si le moteur Xml est en train de calculer les dépendances
     * @returns
     */
    public IsRelatedCalculationInProgress(): boolean {
        return this._relatedCalculationInProgress;
    }

    /**
     * Indique que les valeurs par défaut sont en train de déclancher des "OnChange"
     * @returns
     */
    public IsDefaultValueTriggerOnChange(): boolean {
        return this._defaultValueTriggerOnChange;
    }

    /*-- CREATION ----------------------------------------------------------------------------
    '- Auteur      : LBRI
    '- Date        : 23/03/2006
    '- Description : Evalue la valeur par defaut / filtre / design du controle donne et de tous ses controles 
    '-               qu'il doit mettre a jour.
    /*-- CREATION ----------------------------------------------------------------------------
    '- Auteur      : ABON
    '- Date        : 2009/07/21
    '- Description : Fusion des deux anciennes fonction iws_calculateDefaultValue et iws_checkFilter
    '-- PARAMETRES ---------------------------------------------------------------------------
    ' ENTREE
    '   -> ctrl : le controle en cours d'evaluation
    '   -> page : la page
    '   -> xmlDataType : le type : Val def  / design /filtre
    '-----------------------------------------------------------------------------------------*/
    private _calculateXmlValue(ctrl: IwsControle, page: IwsPage, xmlDataType: string, xmlBuffer: IsiXmlBuffer, xmlTrc: XmlTrace): void {

        let fifo = new IwsFifo();
        let aCtrls: Array<IwsControle> = [];
        let evaluatedCtrl: IwsControle;
        let xmlData;
        let needPush = false;

        this._log.log('_calculateXmlValue -- ' + xmlDataType + ' -- ' + ctrl.id);
        this._newAlgoXmlSort = window.newAlgoXmlSort;

        // ajoute le controle donne pour servir de racine a l'evaluation de l'arborescence en 
        // largeur d'abord
        fifo.put(ctrl);

        // on boucle sur la file, tant qu'elle n'est pas vide.
        while (!fifo.empty()) {
            needPush = false;

            // extrait le prochain element de la file.
            evaluatedCtrl = fifo.get();
            xmlData = evaluatedCtrl[xmlDataType];
            if (xmlData != null) {
                // si le control n'a pas ete evalue alors
                // LBRI - 2010.11.15 - [DYSF:39572] : evaluationDone n'est plus sur le controle mais dans IsiXmlData.
                if (!xmlData.evaluationDone) {
                    // on verifie si on peut faire l'evaluation de cette val def
                    // suivant son type

                    switch (xmlDataType) {
                        case TYPE_VALDEF:

                            // si la Val def doit toujours etre evaluee ou si la valeur du controle est vide
                            var bHasNullValue = (evaluatedCtrl.valueWithContext({ isValdef: true }) === "");
                            if (xmlData.aEval || bHasNullValue) {
                                needPush = true;
                            }

                            // si le controle n'est pas a AlwaysEvaluate et qu'il
                            // n'a pas de valeur, on le note temporairement, afin de
                            // savoir si c'est le calcul des val def qui genere cette valeur
                            if (!xmlData.aEval && bHasNullValue) {
                                // on ne s'occupe pas de savoir si le controle est deja reference
                                // ca serait trop long de le chercher dedans alors que l'information
                                // importante est le fait d'etre dans le tableau ou non.
                                if (!this._aContainsB(page.nullCtrlOnLoad, evaluatedCtrl.id)) {
                                    page.nullCtrlOnLoad.push(evaluatedCtrl.id);
                                }
                            }

                            break;

                        case TYPE_DESIGN:
                            needPush = true;
                            break;

                        case TYPE_FILTER:
                            // on evalue si le filtre doit l'etre
                            if (xmlData.chk || evaluatedCtrl.ctrlType == CTRL_ISIDATALISTCTRL) {
                                needPush = true;
                            }
                            break;
                    }

                    if (needPush) {
                        // on stocke le controle comme devant etre evalue
                        if (!xmlData.evaluationStandBy) {
                            xmlData.evaluationStandBy = true;
                            aCtrls.push(evaluatedCtrl);
                        }
                    }
                }
            }
            // maintenant que le controle a ete mis a jour, il faut demander
            // l'evaluation des controles a mettre a jour.
            this._prepareCtrlsToUpdate(xmlDataType, evaluatedCtrl, fifo, page, xmlTrc);
        }

        const iTop = aCtrls.length;
        if (iTop > 0) {
            // on trie le tableau des controles a mettre a jour
            aCtrls = aCtrls.sort(iwsAllCtrls.sortByLevel[xmlDataType]);
            //this._trcFifo(ctrl.id, xmlDataType, aCtrls);
            // maintenant qu'on connait les controles a mettre a jour,
            // on les met a jour	
            for (let i = 0; i < iTop; ++i) {
                evaluatedCtrl = aCtrls[i];
                xmlData = evaluatedCtrl[xmlDataType];
                if (xmlData != null) {

                    switch (xmlDataType) {

                        case TYPE_VALDEF:
                            // LBRI - 2009.04.07 - [DYSF:25046] : on ne lance l'evaluaton que sur les controles qui n'ont pas
                            // deja ete calcule.
                            // LBRI - 2009.10.06 - [DYSF:xxxxx] : on lance l'evaluation sur tous les controles.
                            // evaluation effective de la valeur par defaut.
                            xmlData.stopPropagation = true;
                            this._evaluateXml(ctrl, evaluatedCtrl, xmlDataType, XmlUserSettingsResult.XML_DEFAULT_VALUE, xmlBuffer, xmlTrc);
                            break;

                        case TYPE_FILTER:
                            // evaluation effective du filtre.
                            if (evaluatedCtrl.valueWithContext({ isValdef: true }) != "" || evaluatedCtrl.ctrlType == CTRL_ISIDATALISTCTRL) {
                                this._evaluateXml(ctrl, evaluatedCtrl, xmlDataType, XmlUserSettingsResult.XML_FILTER_EXECUTE, xmlBuffer, xmlTrc);
                            }
                            break;

                        case TYPE_DESIGN:
                            // on ne lance l'evaluaton que sur les controles qui n'ont pas
                            // deja ete calcule.
                            if (!xmlData.stopPropagation) {
                                // evaluation du design
                                // xmlData.stopPropagation = true; //By ABON
                                this._evaluateXml(ctrl, evaluatedCtrl, xmlDataType, XmlUserSettingsResult.XML_DESIGN, xmlBuffer, xmlTrc);
                            }
                            break;
                    }
                    xmlData.evaluationStandBy = false;
                }
                //on efface la reference
                delete aCtrls[i];
            }
            xmlData = null;
        }
    }

    /**
     * Trace le résultat de l'aglo de tri de l'évaluation des Xml
     * @param triggerId
     * @param xmlDataType
     * @param ctrls
     */
    private _trcFifo(triggerId: string, xmlDataType: string, ctrls: Array<IwsControle>) {
        if (this._traceMode != xmlDataType) return;
        this._trc(xmlDataType, "_trcFifo ========== pour " + triggerId + " ==========");
        ctrls.forEach((value: IwsControle, index: number, array: IwsControle[]) => {
            const df = value[xmlDataType];
            this._trc(xmlDataType, "_trcFifo " + triggerId + " -> " + value.id + ", level=" + df.evaluationLevel + ", updaters=" + df.updaters.join(", "));
        })
    }
    /**
     * Trace spécifique pour l'aglo de tri de l'évaluation des Xml
     * @param xmlDataType
     * @param txt
     */
    private _trc(xmlDataType: string, txt: string) {
        if (this._traceMode != xmlDataType) return;
        uiCore.log("XmlSort: " + xmlDataType + " " + txt);
    }

    /*-- CREATION ----------------------------------------------------------------------------
    '- Auteur      : ABON
    '- Date        : 2009/07/21
    '- Description : Ajoute les elements impactes par la mofication du filtre/valdef/design
    ''-- [ABON] Created 2009/07/21 [TACHE: 26145] 
    '-- PARAMETRES ---------------------------------------------------------------------------
    ' ENTREE
    '   -> xmlDataType : le type : Val def  / design /filtre
    '   -> evaluatedCtrl : le controle en cours d'evaluation
    '   -> fifo :  liste d'appel
    '   -> page : la page
    '-----------------------------------------------------------------------------------------*/
    private _prepareCtrlsToUpdate(xmlDataType: string, evaluatedCtrl: IwsControle, fifo: IwsFifo, page: IwsPage, xmlTrc: XmlTrace): void {

        const xmlData = evaluatedCtrl[xmlDataType];
        if (xmlData == null) return;

        const ctrlsToUpdate = xmlData.ctrlU;
        if (ctrlsToUpdate == null) return;

        //this._trc(xmlDataType, "_prepareCtrlsToUpdate ========== pour " + evaluatedCtrl.id + " (nouvel algo : " + this._newAlgoXmlSort + ") ==========");

        for (let oToUpdateCtrl in ctrlsToUpdate) {
            var toUpdateCtrl = page.getCtrl(ctrlsToUpdate[oToUpdateCtrl].Value);
            //ARAN 02/06/2010 [DYSF36210]On test si toUpdateCtrl est defini avant de continuer
            if (toUpdateCtrl) {
                var xmlDataToUpdateCtrl = toUpdateCtrl[xmlDataType];
                // si le controle a mettre a jour et celui qui met a jour sont un seul et meme
                // controle, il n'est pas utile de creer un lien.
                if (toUpdateCtrl && evaluatedCtrl !== toUpdateCtrl && xmlDataToUpdateCtrl) {
                    // LBRI - 2009.04.07 - [DYSF:25046] : lorsqu'on teste si le controle evaluatedCtrl doit
                    // mettre a jour le controle toUpdateCtrl, on verifie que le lien inverse n'existe
                    // pas deja. C'est a dire que toUpdateCtrl mette a jour evaluatedCtrl, c'est a dire que
                    // toUpdateCtrl soit dans la collection d'updaters de evaluatedCtrl.
                    // LBRI - 2009.09.04 - [DYSF:30313] : lorsqu'un lien existe deja entre deux controles
                    // on n'en ajoute pas de nouveau.
                    // LBRI - 2010.11.15 - [DYSF:39572] : evaluationDone n'est plus sur le controle mais dans IsiXmlData.
                    if (!xmlDataToUpdateCtrl.evaluationDone) {
                        // si le lien inverse existe deja on n'ajoute pas le lien direct
                        if (!this._aContainsB(xmlData.updaters, toUpdateCtrl.id)) {
                            // si le lien direct existe deja, on ne l'ajoute pas
                            if (!this._aContainsB(xmlDataToUpdateCtrl.updaters, evaluatedCtrl.id)) {

                                // toUpdateCtrl = source
                                // evaluatedCtrl = cible
                                // lien : toUpdateCtrl -> evaluatedCtrl
                                if (this._newAlgoXmlSort) {
                                    this._newAlgoflash(evaluatedCtrl, xmlData, fifo, toUpdateCtrl, xmlDataToUpdateCtrl, xmlDataType);
                                } else {
                                    this._oldAlgoFlash(evaluatedCtrl, xmlData, fifo, toUpdateCtrl, xmlDataToUpdateCtrl, xmlDataType);
                                }

                            } else {
                                if (xmlTrc) {
                                    xmlTrc.pushOverDirectLinkTrace(toUpdateCtrl.id, evaluatedCtrl.id);
                                    //this._trc(xmlDataType, "_prepareCtrlsToUpdate : lien directe " + evaluatedCtrl.id + " est déjà dans les updaters de " + toUpdateCtrl.id);
                                }
                            }
                        } else {
                            if (xmlTrc) {
                                //_prepareCtrlsToUpdate : lien inverse itxtSCAN_ACTION est déjà dans les updaters de itxtSCAN_FONCTION
                                xmlTrc.pushReverseLinkTrace(evaluatedCtrl.id, toUpdateCtrl.id);
                            }
                            //this._trc(xmlDataType, "_prepareCtrlsToUpdate : lien inverse " + toUpdateCtrl.id + " est déjà dans les updaters de " + evaluatedCtrl.id);
                        }
                    }
                }
            }
        }
    }

    private _oldAlgoFlash(evaluatedCtrl: IwsControle, xmlData: any, fifo: IwsFifo, toUpdateCtrl: IwsControle, xmlDataToUpdateCtrl: any, xmlDataType: string) {
        // _oldAlgoFlash : ajout de (cible) itxtSCAN_ACTION dans les updaters de (source) itxtSCAN_FONCTION
        //this._trc(xmlDataType, "_oldAlgoFlash : ajout de (cible) " + evaluatedCtrl.id + " dans les updaters de (source) " + toUpdateCtrl.id);
        //this._trc(xmlDataType, "_oldAlgoFlash : le niveau de " + toUpdateCtrl.id + ", passe de " + xmlDataToUpdateCtrl.evaluationLevel + " à " + (xmlData.evaluationLevel + 1).toString());

        // on indique que le controle courant demande l'evaluation du controle toUpdateCtrl.
        xmlDataToUpdateCtrl.updaters.push(evaluatedCtrl.id);
        // on calcule le niveau dans l'arbre du controle a mettre a jour
        xmlDataToUpdateCtrl.evaluationLevel = xmlData.evaluationLevel + 1;
        // on ajoute toUpdateCtrl dans la file.
        //this._trc(xmlDataType, "_oldAlgoFlash : ajout de " + toUpdateCtrl.id + " dans la pile fifo");
        fifo.put(toUpdateCtrl);
    }

    private _newAlgoflash(evaluatedCtrl: IwsControle, xmlData: any, fifo: IwsFifo, toUpdateCtrl: IwsControle, xmlDataToUpdateCtrl: any, xmlDataType: string) {

        const newLevel = xmlData.evaluationLevel + 1;

        //this._trc(xmlDataType, "_newAlgoflash : ajout de (cible) " + evaluatedCtrl.id + " dans les updaters de (source) " + toUpdateCtrl.id);
        //this._trc(xmlDataType, "_newAlgoflash : le niveau de " + toUpdateCtrl.id + ", passe de " + xmlDataToUpdateCtrl.evaluationLevel + " à " + (newLevel).toString() + " (si supérieur)");

        // on indique que le controle courant demande l'evaluation du controle toUpdateCtrl.        
        xmlDataToUpdateCtrl.updaters.push(evaluatedCtrl.id);
        xmlData.updating.push(toUpdateCtrl);

        if (xmlDataToUpdateCtrl.evaluationLevel < newLevel) {
            const delta = newLevel - xmlDataToUpdateCtrl.evaluationLevel;
            // on calcule le niveau dans l'arbre du controle a mettre a jour
            xmlDataToUpdateCtrl.evaluationLevel = newLevel;

            //this._trc(xmlDataType, "_newAlgoflash : augmente le niveau de la valeur " + evaluatedCtrl.id + ".evaluationLevel=" + xmlData.evaluationLevel + " + 1");
            //this._trc(xmlDataType, "_newAlgoflash : vers la valeur " + toUpdateCtrl.id + ".evaluationLevel=" + xmlDataToUpdateCtrl.evaluationLevel);

            // il faut répercuter le delta sur tous les "updatés" de xmlDataToUpdateCtrl                              
            this._repercuteDelta(xmlDataType, delta, toUpdateCtrl);
        }

        // on ajoute toUpdateCtrl dans la file.
        //this._trc(xmlDataType, "_newAlgoflash : ajout de " + toUpdateCtrl.id + " dans la pile fifo");
        fifo.put(toUpdateCtrl);
    }

    /**
     * Répercute l'augmentation de niveau sur tous les contrôles que le contrôle racine doit mettre à jour
     * @param xmlDataType Le type de donnée XML
     * @param delta La valeur de l'augmentation
     * @param root Le contrôle racine
     */
    private _repercuteDelta(xmlDataType: string, delta: number, root: IwsControle) {
        if (delta > 0) {
            const ctrlsDone = {};
            this._rec_repercuteDelta(xmlDataType, delta, root, ctrlsDone);
        }
    }

    /**
     * Fonction récursive qui ajoute le delta à tous les contrôles en remontant les collections de "updating"
     * @param xmlDataType Le type de donnée XML
     * @param delta La valeur de l'augmentation
     * @param root Le contrôle racine
     * @param ctrlsDone La collection de contrôles déjà traités par la récursion (sert à éviter que les boucles n'empêche la fin du traitement)
     */
    private _rec_repercuteDelta(xmlDataType: string, delta: number, root: IwsControle, ctrlsDone: any) {
        const xmlData = root[xmlDataType];
        if (xmlData) {

            xmlData.updating.forEach((value: IwsControle) => {
                // on ne traite que les contrôles qui ne l'ont pas déjà été
                if (typeof (ctrlsDone[value.id]) === "undefined") {
                    const xml = value[xmlDataType];
                    xml.evaluationLevel += delta;
                    ctrlsDone[value.id] = value;

                    //this._trc(xmlDataType, "_rec_repercuteDelta " + value.id + " est augmenté de " + delta + " pour passer à " + xml.evaluationLevel);

                    this._rec_repercuteDelta(xmlDataType, delta, value, ctrlsDone);
                }

            });
        }
    }

    /**
     * Indique qu'il faudra reevaluer les tooltips dynamiques lies au controle modifie
     * @param ctrl le controle en cours de modification
     * @param page la page
     * @returns
     */
    private _dropXmlTooltip(ctrl: IwsControle, page: IwsPage): void {
        // DGRA 16/05/2011
        const xmlData = ctrl[TYPE_TOOLTIP];
        if (!xmlData) {
            // si pas de tooltip dynamique, reinitialisation du controle pour afficher sa valeur en infobulle
            if (ctrl.tooltip) {
                ctrl.setTooltip('###'); // Cette valeur indique que c'est la valeur du champ qui sera affichée dans le tooltip
            }
            return;
        }

        // reinitialisation des infobulles dynamiques qui utilisent ce controle.
        const ctrlsToUpdate = xmlData.ctrlU;
        if (ctrlsToUpdate == null) return;

        for (let oToUpdateCtrl in ctrlsToUpdate) {
            var toUpdateCtrl = page.getCtrl(ctrlsToUpdate[oToUpdateCtrl].Value);
            if (toUpdateCtrl) {
                toUpdateCtrl.setTooltip(XmlUserSettingsResult.XML_TOOLTIP);
            }
        }
        return;
    }

    private async _callAsyncValDefInSearchState(ctrl: IwsControle) {

        this._iurc_depth++;
        this._log.log('****' + this._iurcDepth() + ' Debut -- _callAsyncValDefInSearchState -- ' + ctrl.id + ' -- Search -- niveau.iurc(inc)=' + this._iurc_depth);

        iwsAllCtrls.dropUpdaters();

        const xmlBuffer: IsiXmlBuffer = new IsiXmlBuffer();

        //on traite le design
        this.setEvaluationDone(ctrl, TYPE_DESIGN, true);
        this._calculateXmlValue(ctrl, iwsAllCtrls, TYPE_DESIGN, xmlBuffer, null);

        await xmlBuffer.flush(this._uniqueId, this.ctrlsDesignEvaluated);

        this._log.log('****' + this._iurcDepth() + ' Fin   -- _callAsyncValDefInSearchState -- ' + ctrl.id + ' -- Search -- niveau.iurc(dec)=' + this._iurc_depth);
        this._iurc_depth--;

        return "on.stop";

    }

    private async _callAsyncValDef(ctrl: IwsControle) {

        let xmlTrc: XmlTrace;
        let xmlBuffer: IsiXmlBuffer;

        this._iurc_depth++;

        this._log.log('****' + this._iurcDepth() + ' Debut -- _callAsyncValDef -- ' + ctrl.id + ' -- niveau.iurc(inc)=' + this._iurc_depth + ' -- rcip=' + this._relatedCalculationInProgress);

        // prise en compte du changement de valeur du contrôle : permet de connaitre les contrôles qui ont été modifiés
        isiApp.ctrls.onChange(ctrl);

        //Cette partie permet d'évaluer le design des controles impactés par un onChange
        if (this._relatedCalculationInProgress) {

            //Si le design à déjà été évalué au premier passage, on ne le réévalue pas suite au onChange 
            if (typeof (this.ctrlsDesignEvaluated[ctrl.id]) === "undefined") {
                xmlBuffer = new IsiXmlBuffer();
                //on traite le design si le controle a change de valeur s'il s'agit d'un changement de valeur valdef
                if (uiCore.getIsModeDiag()) { xmlTrc = new XmlTrace(TYPE_DESIGN); }
                this._calculateXmlValue(ctrl, iwsAllCtrls, TYPE_DESIGN, xmlBuffer, xmlTrc);
                if (uiCore.getIsModeDiag()) { xmlTrc.dumpLinkTraces(this._uniqueId); }
                await xmlBuffer.flush(this._uniqueId, this.ctrlsDesignEvaluated);
            }

            this._log.log('****' + this._iurcDepth() + ' Fin   -- _callAsyncValDef -- ' + ctrl.id + ' -- relatedCalculationInProgress -- niveau.iurc(dec)=' + this._iurc_depth);
            this._iurc_depth--;

            if (this._evalCount <= 1) {
                this._updateRelatedCalculationInProgress(false);
                this.ctrlsDesignEvaluated = {};
                await iwsAllCtrls.finishUrc(ctrl);
            }
            return "on.rcip";
        }

        // on specifie qu'on est en train de faire le calcul des controles lies.
        this._updateRelatedCalculationInProgress(true);
        this.ctrlsDesignEvaluated = {};

        // si c'est une modification utilisateur, on le definit comme tel.
        if (!ctrl.isChangingByCodeAction) {
            ctrl.setAsChangedByUser(true);
        }

        // on supprime toutes les references aux controles qui mettent a jour.
        iwsAllCtrls.dropUpdaters();

        xmlBuffer = new IsiXmlBuffer();

        // on marque le controle comme etant evalue
        this.setEvaluationDone(ctrl, TYPE_VALDEF, true);

        if (uiCore.getIsModeDiag()) { xmlTrc = new XmlTrace(TYPE_VALDEF); }
        this._calculateXmlValue(ctrl, iwsAllCtrls, TYPE_VALDEF, xmlBuffer, xmlTrc);
        if (uiCore.getIsModeDiag()) { xmlTrc.dumpLinkTraces(this._uniqueId); }

        // on refait la suppression de toutes les references aux controles qui mettent a jour.
        // car la mise a jour par les valeurs par defaut n'est pas la meme que pour les filtres.
        iwsAllCtrls.dropUpdaters();

        // Revalidation des filtres sur les controles.
        this.setEvaluationDone(ctrl, TYPE_FILTER, true);
        if (uiCore.getIsModeDiag()) { xmlTrc = new XmlTrace(TYPE_FILTER); }
        this._calculateXmlValue(ctrl, iwsAllCtrls, TYPE_FILTER, xmlBuffer, xmlTrc);
        if (uiCore.getIsModeDiag()) { xmlTrc.dumpLinkTraces(this._uniqueId); }

        iwsAllCtrls.dropUpdaters();

        //on traite le design
        this.setEvaluationDone(ctrl, TYPE_DESIGN, true);
        if (uiCore.getIsModeDiag()) { xmlTrc = new XmlTrace(TYPE_DESIGN); }
        this._calculateXmlValue(ctrl, iwsAllCtrls, TYPE_DESIGN, xmlBuffer, xmlTrc);
        if (uiCore.getIsModeDiag()) { xmlTrc.dumpLinkTraces(this._uniqueId); }

        iwsAllCtrls.dropUpdaters();

        // on a termine l'evaluation complete, on restitue l'etat
        this.setEvaluationDone(ctrl, TYPE_VALDEF, false);
        this.setEvaluationDone(ctrl, TYPE_FILTER, false);
        this.setEvaluationDone(ctrl, TYPE_DESIGN, false);

        await xmlBuffer.flush(this._uniqueId, this.ctrlsDesignEvaluated); // set value de tous les contrôles qui dépende de ctrl et lance tous les onchange

        if (this._evalCount <= 1) {
            this._updateRelatedCalculationInProgress(false);
        }

        // LBRI - 2009.10.01 - [DYSF:31599] : PERFS : ajout d'un evenement lance a la fin
        // des evaluations des "UptadeRelatedControl".
        // le compteur sert a empecher la fonction d'etre reentrante.
        await iwsAllCtrls.finishUrc(ctrl);

        this._log.log('****' + this._iurcDepth() + ' Fin   -- _callAsyncValDef -- ' + ctrl.id + '-- niveau.iurc(dec)=' + this._iurc_depth);
        this._iurc_depth--;

        return "on.stop";
    }

    private _iurcDepth(): string {
        return "".padEnd(4 * this._iurc_depth, '*') + ">";
    }

    /*-- CREATION -----------------------------------------------------------------------------
'- Auteur      : LBRI
'- Date        : 24/03/2006
'- Description : Indique si un element est dans un tableau.
'-----------------------------------------------------------------------------------------*/
    private _aContainsB(a: Array<any>, b: any): boolean {

        if (!a) {
            return false;
        }
        if (!b) {
            return false;
        }
        if (!a.length) {
            return false;
        }

        let _finded = false;
        const _last = a.length;
        let i = 0;
        while (!_finded && i < _last) {
            _finded = (a[i++] == b);
        }

        return _finded;
    }


    /**
     * Met à jour la propriété _relatedCalculationInProgress et lance un event pour indiquer qu'il n'y a plus de traitement XML en cours
     * @param value
     */
    private _updateRelatedCalculationInProgress(value: boolean) {
        this._relatedCalculationInProgress = value
        if (!value) {
            this.dispatchEvent(new Event(this._eventRelatedCalculationFinish));
        }
    }

    /**
     * Permet de s'abonner à l'event indiquant que les traitements XML ont finis de s'exécuter
     * @param handler 
     * @param once indique si l'on souhaite supprimé l'abonnement une fois que l'event a été lancé une fois
     */
    public add_onRelatedCalculFinish(handler: () => void, once: boolean) {
        this.addEventListener(this._eventRelatedCalculationFinish, () => handler(), { once: once });
    }

    /**
     * Permet de se désabonner à l'event indiquant que les traitements XML ont finis de s'exécuter
     * @param handler
     */
    public remove_onRelatedCalculFinish(handler: () => void) {
        this.removeEventListener(this._eventRelatedCalculationFinish, () => handler());
    }


}

/**
 * Permet d'avoir accès à la classe IsiXmlUserSettings depuis le javascript
 * @returns
 */
export function buildXmlUserSettings() {
    return new IsiXmlUserSettings();
}
window.buildXmlUserSettings = buildXmlUserSettings;
